import { intersection } from 'lodash';
import { Navigate, useLocation } from 'react-router-dom';
import React from 'react';

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';
const INTENTIONAL_LOGOUT_KEY = 'intentionalLogout';

/** Only allow same-app relative paths (blocks open redirects). */
export function sanitizeRedirectPath(path) {
    if (!path || typeof path !== 'string') return null;

    let decoded = path.trim();
    try {
        decoded = decodeURIComponent(decoded);
    } catch {
        // keep original trimmed value
    }

    if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
    if (decoded.includes('://')) return null;
    if (decoded === '/login' || decoded.startsWith('/auth')) return null;

    return decoded;
}

export function rememberPostLoginRedirect(path) {
    if (sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY)) {
        return null;
    }
    const safe = sanitizeRedirectPath(path);
    if (safe && safe !== '/') {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, safe);
        return safe;
    }
    return null;
}

export function clearPostLoginRedirect() {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
}

/** Call on explicit logout so the current page is not restored after login. */
export function markIntentionalLogout() {
    sessionStorage.setItem(INTENTIONAL_LOGOUT_KEY, '1');
    clearPostLoginRedirect();
}

export function consumePostLoginRedirect(search) {
    if (sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY)) {
        sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
        clearPostLoginRedirect();
        return null;
    }

    let fromQuery = null;
    if (typeof search === 'string') {
        const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
        fromQuery = sanitizeRedirectPath(params.get('redirect'));
    } else if (search && typeof search.get === 'function') {
        fromQuery = sanitizeRedirectPath(search.get('redirect'));
    } else if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        fromQuery = sanitizeRedirectPath(params.get('redirect'));
    }

    const fromSession = sanitizeRedirectPath(sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY));
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

    const target = fromQuery || fromSession;
    return target && target !== '/' ? target : null;
}

/** Build /auth/login URL and remember where to return after login. */
export function buildLoginPath(redirectPath) {
    if (sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY)) {
        sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
        clearPostLoginRedirect();
        return '/auth/login';
    }

    const path =
        redirectPath != null
            ? redirectPath
            : typeof window !== 'undefined'
              ? `${window.location.pathname}${window.location.search}${window.location.hash}`
              : null;
    const safe = rememberPostLoginRedirect(path);
    if (!safe) return '/auth/login';
    return `/auth/login?redirect=${encodeURIComponent(safe)}`;
}

export function CheckStore({ component: RouteComponent }) {
    const location = useLocation();
    if (!localStorage.getItem('user')) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        return <Navigate to={buildLoginPath(redirect)} replace />;
    }
    return <RouteComponent />;
}

export function isLoggedIn() {
    let user = localStorage.getItem('user');
    if (user === null || user === undefined || user === 'undefined') {
        localStorage.removeItem('user');
        return false;
    }
    let tok = JSON.parse(user);
    return tok && tok.user && tok.access_token && tok.roles;
}

export function isAdmin() {
    let user = localStorage.getItem('user');
    if (user === null || user === undefined || user === 'undefined') {
        localStorage.removeItem('user');
        return false;
    }
    let tok = JSON.parse(user);
    return tok && tok.roles && tok.roles.includes('super_admin');
}

export function isArrayWithLength(arr) {
    return Array.isArray(arr) && arr.length;
}

export function getAllowedRoutes(routes) {
    const userStore = JSON.parse(localStorage.getItem('user'));
    if (!userStore) return [];
    const userPermissions = userStore.permission;
    const userGroupTypes = userStore.groupTypes;
    const userRoles = userStore.roles || [];
    const isSuperAdmin = userRoles.includes('super_admin');

    return routes.filter(({ permission, showByGroupType, roles, showByCondition }) => {
        if (
            showByGroupType &&
            userGroupTypes &&
            intersection(showByGroupType, userGroupTypes).length === 0
        )
            return false;

        // Explicit role restriction — applies to everyone including super admins
        if (roles && roles.length > 0 && intersection(roles, userRoles).length === 0)
            return false;

        // Super admins bypass permission checks — they have access to everything
        // unless explicitly restricted via the roles array above
        if (isSuperAdmin) return true;

        if (showByCondition && showByCondition(userStore)) return true;

        if (!permission || permission.length === 0) return false;
        return intersection(permission, userPermissions).length > 0;
    });
}

export function checkMenuItemConditions(menuItem, userStore) {
    if (menuItem.showByGroupType && userStore.groupTypes) {
        const intersection = menuItem.showByGroupType.filter((type) =>
            userStore.groupTypes.includes(type)
        );
        if (intersection.length === 0) {
            return false;
        }
    }

    // Athuga tímasetningu
    if (!checkTimeBasedConditions(menuItem, userStore)) {
        return false;
    }

    if (menuItem.showByCondition) {
        return menuItem.showByCondition(userStore);
    }

    if (menuItem.permission && userStore.permission) {
        const hasPermission = menuItem.permission.some((perm) =>
            userStore.permission.includes(perm)
        );
        if (!hasPermission) {
            return false;
        }
    }

    return true;
}

export function checkTimeBasedConditions(menuItem, userStore) {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay(); // 0 = sunnudagur, 1 = mánudagur, etc.

    if (menuItem.showByTime) {
        const { startHour, endHour, daysOfWeek } = menuItem.showByTime;

        if (daysOfWeek && !daysOfWeek.includes(currentDay)) {
            return false;
        }

        if (startHour !== undefined && endHour !== undefined) {
            if (currentHour < startHour || currentHour >= endHour) {
                return false;
            }
        }
    }

    return true;
}

export function checkAttachedGroupConditions(userStore) {
    if (!userStore.groupTypes || !userStore.groupTypes.includes('golden')) {
        return false;
    }

    if (!userStore.permission.includes('cu-group-update')) {
        return false;
    }

    if (userStore.user && userStore.user.is_active === false) {
        return false;
    }

    if (userStore.user && userStore.user.settings) {
        if (userStore.user.settings.show_attached_groups === false) {
            return false;
        }
    }

    return true;
}

export const AuthRoute = ({ component: RouteComponent }) => {
    const location = useLocation();
    let data = JSON.parse(localStorage.getItem('user'));
    if (data) {
        let isAuthenticated = !!(
            data &&
            data.user &&
            data.access_token &&
            data.roles &&
            data.permission
        );
        if (isAuthenticated && !sessionStorage.getItem('justTransitioning')) {
            const redirect = consumePostLoginRedirect(location.search);
            return <Navigate to={redirect || '/'} replace />;
        } else {
            return <RouteComponent />;
        }
    } else {
        return <RouteComponent />;
    }
};
